import React, { useEffect, useState } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import { Category, TalkTimeComparisonResponse, TalkCompareRow, getTalkCompare } from '../../api';
import { LoadingSpinner } from '../../../../components/common/LoadingSpinner';
import { CategoryMultiSelect } from './CategoryMultiSelect';

interface TalkTimeComparisonProps {
  from: string;
  to: string;
  categories: Category[];
}

const axisTick = { fill: '#94a3b8', fontSize: 12 };

const fmtDur = (seconds: number | null) => {
  if (seconds == null) return '—';
  const s = Math.round(seconds);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

// One horizontal bar chart of AvgTalkSeconds per agent, plus a plain-text list underneath
// (every row, chart-plottable or not) so the sample size is readable without relying on SVG
// tooltips — charts alone aren't a reliable a11y surface, and rows with no duration data at
// all (AvgTalkSeconds null) can't be plotted as a bar in the first place (OQ-8: shown, not
// suppressed, either way).
const TalkBarChart: React.FC<{ rows: TalkCompareRow[]; emptyLabel: string }> = ({ rows, emptyLabel }) => {
  const chartData = rows
    .filter(r => r.AvgTalkSeconds != null)
    .map(r => ({ name: r.DisplayName ?? `#${r.AgentID}`, avg: r.AvgTalkSeconds as number, n: r.CallCount }));
  if (rows.length === 0) {
    return <div className="flex h-16 items-center justify-center text-sm text-muted">{emptyLabel}</div>;
  }
  return (
    <div>
      {chartData.length > 0 && (
        <div style={{ height: Math.max(200, chartData.length * 32 + 40) }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} layout="vertical" margin={{ top: 8, right: 24, left: 8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" horizontal={false} />
              <XAxis type="number" tick={axisTick} tickFormatter={(v) => fmtDur(Number(v))} tickLine={false} axisLine={{ stroke: '#334155' }} />
              <YAxis type="category" dataKey="name" width={140} interval={0} tick={axisTick} tickLine={false} axisLine={{ stroke: '#334155' }} />
              <Tooltip
                cursor={{ fill: 'rgba(148,163,184,0.15)' }}
                formatter={(v: any, _n: any, item: any) => [`${fmtDur(Number(v))} (n=${item?.payload?.n})`, 'Avg talk time']}
              />
              <Bar dataKey="avg" fill="#2563eb" radius={[0, 3, 3, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
      <ul className="mt-2 space-y-0.5 text-xs text-secondary">
        {rows.map(r => (
          <li key={r.AgentID}>
            {r.DisplayName ?? `#${r.AgentID}`}: {fmtDur(r.AvgTalkSeconds)} (n={r.CallCount})
          </li>
        ))}
      </ul>
    </div>
  );
};

// FR Group 4 — average talk time compared across agents overall, and (per FR-4.2) broken out
// per category. A call's duration counts once per assigned category in the per-category view,
// so per-category totals can exceed the overall figures (footnoted, per OQ-10).
export const TalkTimeComparison: React.FC<TalkTimeComparisonProps> = ({ from, to, categories }) => {
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>([]);
  const [data, setData] = useState<TalkTimeComparisonResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    return getTalkCompare(from, to, selectedCategoryIds)
      .then(setData)
      .catch(() => setError('Could not load the talk-time comparison — try again.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [from, to, selectedCategoryIds]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <h3 className="mb-2 text-sm font-semibold text-secondary">Talk Time Comparison</h3>

      <div aria-live="polite" role="status" className="sr-only">
        {loading ? 'Loading talk time comparison…' : ''}
      </div>

      {loading && <LoadingSpinner size="sm" message="Loading talk time comparison…" />}
      {!loading && error && (
        <div role="alert" className="rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">
          {error}
        </div>
      )}
      {!loading && !error && data && (
        <>
          <h4 className="mb-2 text-sm font-medium text-primary">Overall — average talk time per agent</h4>
          <TalkBarChart rows={data.Overall} emptyLabel="No answered calls with duration data in range." />

          <div className="my-6 border-t border-border-default pt-4">
            <h4 className="mb-2 text-sm font-medium text-primary">By category</h4>
            <CategoryMultiSelect
              idPrefix="talkcompare"
              categories={categories}
              selected={selectedCategoryIds}
              onChange={setSelectedCategoryIds}
            />
            <div className="mt-4 space-y-6">
              {data.ByCategory.map(cat => (
                <div key={cat.CategoryID}>
                  <h5 className="mb-2 text-sm font-medium text-primary">{cat.CategoryName}</h5>
                  <TalkBarChart rows={cat.Rows} emptyLabel="No answered calls with duration data for this category in range." />
                </div>
              ))}
              {data.ByCategory.length === 0 && (
                <p className="text-sm text-muted">No categorized calls in range.</p>
              )}
            </div>
            <p className="mt-3 text-xs text-muted">
              A call with multiple categories counts once per category here — per-category totals
              may exceed the overall figures above.
            </p>
          </div>
        </>
      )}
    </div>
  );
};
